import {
  brands,
  regular,
  solid,
} from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses, TooltipProps } from "@mui/material/Tooltip";
import classNames from "classnames";
import pluralize from "pluralize";
import React from "react";
import { Link } from "react-router-dom";
import { useIntercom } from "react-use-intercom";

import { IconButtonWithDropdownMenuItem } from "../../common/IconButtonWithDropdown";
import { DropdownLink } from "../../common/module";
import { useBasicData, useShowSnackbar } from "../../hooks";
import { discordUrl, supportUrl } from "../../utils/constants";
import { getConnectionNodes } from "../../utils/relay";
import { capitalize, truncate } from "../../utils/strings";
import { Permission } from "../graphql/customTypes";
import DefaultProfileImage from "./DefaultProfileImage";

type WorkspaceSelectionButtonProps = {
  fixed: boolean;
};

export default function WorkspaceSelectionButton({
  fixed,
}: WorkspaceSelectionButtonProps) {
  const { user, userError, workspace } = useBasicData();
  const memberships = getConnectionNodes(user?.memberships);
  const { showNewMessage } = useIntercom();
  const { showError } = useShowSnackbar();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [isWorkspacesListOpen, setIsWorkspacesListOpen] = React.useState(false);
  const isOpen = Boolean(anchorEl);

  const openWorkspaceMenu = (event) => {
    setAnchorEl(event.target);
  };

  const closeWorkspaceMenu = () => {
    setAnchorEl(null);
    setIsWorkspacesListOpen(false);
  };

  const handleIntercomMessage = () => {
    setAnchorEl(null);
    showNewMessage();
  };

  if (userError) {
    showError(userError.message);
    return null;
  }

  if (!workspace || !workspace.myRole) {
    return;
  }

  const currentWorkspaceIsPersonal = workspace.id === user.personalWorkspace.id;

  const workspaceMemberCount =
    workspace.planSeats === 0 ? 1 : workspace.planSeats;

  const administrationOptionsMap = {
    PLANS: {
      id: "plans",
      name: "Manage Plan",
      icon: <FontAwesomeIcon icon={regular("rectangle-pro")} />,
      colors: "text-[inherit] bg-transparent",
      route: `/${workspace.name}/settings/plans`,
      onClick: closeWorkspaceMenu,
    },
    BILLING: {
      id: "billing",
      name: "Billing Options",
      icon: <FontAwesomeIcon icon={regular("credit-card-front")} />,
      colors: "text-[inherit] bg-transparent",
      route: `/${workspace.name}/settings/billing`,
      onClick: closeWorkspaceMenu,
    },
  };

  const administrationOptions = [
    administrationOptionsMap.PLANS,
    administrationOptionsMap.BILLING,
  ];

  const adminOptionsMap = {
    GENERAL: {
      id: "general",
      name: "General Settings",
      icon: <FontAwesomeIcon icon={solid("square-sliders-vertical")} />,
      colors: "text-[inherit] bg-transparent",
      route: `/${workspace.name}/settings/general`,
      onClick: closeWorkspaceMenu,
    },
    MEMBERS: {
      id: "members",
      name: "Manage Members",
      icon: (
        <div className="relative">
          <span className="absolute -top-0.5 -right-1.5 h-3 w-3 bg-layer-100 group-hover:bg-layer-200  dark:bg-layer-800 dark:group-hover:bg-layer-700 rounded-full flex items-center justify-center">
            <FontAwesomeIcon className="text-[0.5rem]" icon={solid("plus")} />
          </span>
          <FontAwesomeIcon icon={solid("square-user")} />
        </div>
      ),
      colors: "text-[inherit] bg-transparent",
      route: `/${workspace.name}/settings/members`,
      onClick: closeWorkspaceMenu,
    },
    PLANS_BILLING: {
      id: "billing-menu",
      name: "Plans & Billing",
      icon: (
        <div className="relative flex items-center justify-center">
          <span className="text-[0.9rem] font-black text-layer-100 group-hover:text-layer-200 dark:text-layer-800 dark:group-hover:text-layer-700 absolute left-0 right-0 mx-auto text-center font-system">
            $
          </span>
          <FontAwesomeIcon icon={solid("square")} />
        </div>
      ),
      colors: "text-[inherit] bg-transparent",
      subMenu: administrationOptions,
    },
  };

  const adminOptions = [];
  if (workspace.myPermissions.includes(Permission.AdminSettings)) {
    adminOptions.push(adminOptionsMap.GENERAL);
  }
  if (
    workspace.myPermissions.includes(Permission.AdminMembers) &&
    !currentWorkspaceIsPersonal
  ) {
    adminOptions.push(adminOptionsMap.MEMBERS);
  }
  if (workspace.myPermissions.includes(Permission.AdminBilling)) {
    adminOptions.push(adminOptionsMap.PLANS_BILLING);
  }

  const moreOptionsMap = {
    SUPPORT: {
      id: "support",
      name: "Layer Docs & FAQs",
      icon: <FontAwesomeIcon icon={regular("circle-question")} />,
      colors: "text-layer-600 dark:text-layer-300 bg-transparent",
      weight: "light",
      route: supportUrl,
      onClick: closeWorkspaceMenu,
    },
    MESSAGE: {
      id: "message",
      name: "Get Support",
      icon: (
        <FontAwesomeIcon
          icon={regular("message-lines")}
          className="translate-y-0.5"
        />
      ),
      colors: "text-layer-600 dark:text-layer-300 bg-transparent",
      weight: "light",
      borderBottom: true,
      onClick: handleIntercomMessage,
    },
    DISCORD: {
      id: "discord",
      name: "Join our Discord",
      icon: (
        <FontAwesomeIcon icon={brands("discord")} className="text-[0.8rem]" />
      ),
      colors: "text-layer-600 dark:text-layer-300 bg-transparent",
      weight: "light",
      route: discordUrl,
      onClick: closeWorkspaceMenu,
    },
  };

  const moreOptions = [
    moreOptionsMap.SUPPORT,
    moreOptionsMap.MESSAGE,
    moreOptionsMap.DISCORD,
  ];

  const teamWorkspaces: DropdownLink[] = memberships
    .filter(
      (membership) => membership.workspace.id !== user.personalWorkspace?.id,
    )
    .sort((a, b) => a.workspace.name.localeCompare(b.workspace.name))
    .map((membership) => {
      return {
        id: membership.workspace.id,
        name: capitalize(truncate(membership.workspace.displayName, 15)),
        icon: membership.workspace.profilePicture ? (
          <img
            className="rounded-full"
            src={membership.workspace.profilePicture}
          />
        ) : (
          <DefaultProfileImage
            workspaceName={membership.workspace.displayName}
            className="overflow-hidden text-xs rounded-full"
          />
        ),
        route: `/${membership.workspace.name}`,
        borderBottom: false,
        onClick: closeWorkspaceMenu,
      };
    });

  teamWorkspaces.push({
    id: "new-workspace",
    name: "Create New Workspace",
    route: `/new`,
    icon: <FontAwesomeIcon icon={regular("plus")} rotation={90} />,
    colors: "text-layer-600 dark:text-layer-300 bg-transparent",
  });
  teamWorkspaces.push({
    id: "manage-memberships",
    name: "Manage Memberships",
    route: `/settings/workspaces`,
    icon: <FontAwesomeIcon icon={regular("buildings")} />,
    colors: "text-layer-600 dark:text-layer-300 bg-transparent",
  });

  const personalWorkspaces = [];

  if (user?.personalWorkspace) {
    personalWorkspaces.push({
      id: user.personalWorkspace.id,
      name: capitalize(truncate(user.personalWorkspace.name, 15)),
      icon: <img src={user.profilePicture} className="rounded-full" />,
      route: `/${user.personalWorkspace.name}`,
      onClick: closeWorkspaceMenu,
    });
  }

  const WorkspacesSubMenu = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip
      {...props}
      id="tooltip_menu"
      classes={{ popper: className }}
      enterDelay={0}
      enterNextDelay={0}
      leaveDelay={500}
      TransitionProps={{
        timeout: {
          enter: 200,
          exit: 100,
        },
        unmountOnExit: true,
      }}
      onClose={() => setIsWorkspacesListOpen(false)}
    />
  ))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
      overflow: "hidden",
      width: "210px",
      minHeight: 32,
      position: "relative",
      top: "-26px !important",
      left: "11px !important",
      paddingBottom: "0px !important",
    },
  }));

  const teamMemberships = memberships
    .filter(
      (membership) => membership.workspace.id !== user.personalWorkspace?.id,
    )
    .filter((membership) => membership.workspace.id !== workspace?.id)
    .sort((a, b) => a.workspace.name.localeCompare(b.workspace.name))
    .map((membership) => {
      return {
        id: membership.workspace.id,
        name: membership.workspace.displayName,
        icon: membership.workspace.profilePicture || null,
        route: `/${membership.workspace.name}`,
      };
    });

  const previewWorkspaces = [];

  if (user?.personalWorkspace && user.personalWorkspace.id !== workspace?.id) {
    previewWorkspaces.push({
      id: user.personalWorkspace.id,
      name: user.personalWorkspace.name,
      logo: user.personalWorkspace.profilePicture || null,
      route: `/${user.personalWorkspace.name}`,
      selector: "personal",
    });
  }

  if (teamMemberships.length > 0) {
    previewWorkspaces.push({
      id: teamMemberships[0].id,
      name: teamMemberships[0].name,
      logo: teamMemberships[0].icon,
      route: teamMemberships[0].route,
      selector: "team0",
    });
  }

  if (teamMemberships.length > 1) {
    previewWorkspaces.push({
      id: teamMemberships[1].id,
      name: teamMemberships[1].name,
      logo: teamMemberships[1].icon,
      route: teamMemberships[1].route,
      selector: "team1",
    });
  }

  if (
    teamMemberships.length > 2 &&
    user.personalWorkspace.id === workspace?.id
  ) {
    previewWorkspaces.push({
      id: teamMemberships[2].id,
      name: teamMemberships[2].name,
      logo: teamMemberships[2].icon,
      route: teamMemberships[2].route,
      selector: "team2",
    });
  }

  return (
    <>
      <div className="flex items-center h-8 max-w-full">
        <Button
          className={classNames({
            "group -ml-px pl-0 pr-2 h-9 rounded-full flex items-center": true,
            "border border-solid": fixed,
            "bg-transparent border-transparent bg-none hide_touch": !fixed,
            "text-layer-800  dark:text-layer-200 dark:bg-layer-800 dark:border-layer-600/25":
              fixed && isOpen,
            "border-layer-300 bg-layer-100": !fixed && isOpen,
            "bg-none bg-transparent border-transparent text-layer-700 hover:text-layer-800 dark:text-layer-200 dark:hover:text-layer-50":
              !isOpen,
            " hover:bg-white hover:border-layer-300 dark:hover:bg-layer-800 dark:hover:border-layer-700":
              fixed && !isOpen,
          })}
          onClick={(event) => openWorkspaceMenu(event)}
          color="inherit"
          data-cy="workspace-selection-button"
          data-tour="workspace-selection-button"
        >
          <picture
            className={classNames({
              "rounded-full transition-all": true,
              "bg-white dark:bg-layer-800": fixed && !isOpen,
              "h-9 w-9 ring-1 ring-inset ring-layer-600 -translate-x-px p-1.5 mr-1.5":
                fixed,
              "flex items-center justify-center bg-layer-900 mr-2": !fixed,
            })}
          >
            <div className="flex items-center justify-center w-6 h-6 overflow-hidden rounded-full shadow-sm dark:shadow-black/50 bg-layer-700">
              {workspace.profilePicture ? (
                <img src={workspace.profilePicture} />
              ) : (
                <DefaultProfileImage
                  workspaceName={workspace.displayName}
                  className="text-sm"
                />
              )}
            </div>
          </picture>

          <span className="flex-1 text-left truncate text-base font-semibold normal-case mr-1.5">
            {workspace.displayName}
          </span>

          <span className="relative px-0.5 pt-px opacity-50 transition-opacity duration-300 ease-in-out group-hover:opacity-100">
            <svg
              width="8.4"
              height="12"
              viewBox="0 0 7 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill="currentColor"
                d="M3.26301 1.56822L3.66235 1.10231L3.26301 0.76001L2.86366 1.10231L3.26301 1.56822ZM3.26301 8.93185L2.86366 9.39776L3.26301 9.74006L3.66235 9.39776L3.26301 8.93185ZM0.798704 4.48867L3.66235 2.03412L2.86366 1.10231L0 3.55686L0.798704 4.48867ZM2.86366 2.03412L5.72731 4.48867L6.52601 3.55686L3.66235 1.10231L2.86366 2.03412ZM0 6.94321L2.86366 9.39776L3.66235 8.46595L0.798704 6.0114L0 6.94321ZM3.66235 9.39776L6.52601 6.94321L5.72731 6.0114L2.86366 8.46595L3.66235 9.39776Z"
              />
            </svg>
          </span>
        </Button>
      </div>

      <Menu
        open={isOpen}
        onClose={closeWorkspaceMenu}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: fixed ? "left" : "center",
        }}
        sx={{
          "& .MuiPaper-root": {
            top: fixed ? "56px !important" : "110px !important",
            left: fixed ? "12px !important" : "8px !important",
            width: fixed ? "210px" : "222px",
          },
        }}
        MenuListProps={{
          // @ts-ignore
          ["data-cy"]: "workspace-selection-menu",
          ["data-tour"]: "workspace-selection-menu",
        }}
      >
        <li className="w-full pt-3.5 px-3.5">
          <div className="w-full flex pt-0.5 pb-2">
            <div className="w-full flex flex-col px-0.5 space-y-2">
              <div className="flex items-start justify-between w-full">
                <div className="flex items-center justify-center w-10 h-10 overflow-hidden -translate-x-1 -translate-y-1 rounded-full shrink-0 bg-layer-700">
                  {workspace.profilePicture ? (
                    <img
                      className="rounded-full select-none"
                      src={workspace.profilePicture}
                    />
                  ) : (
                    <DefaultProfileImage
                      workspaceName={workspace.displayName}
                    />
                  )}
                </div>

                <div className="flex items-center space-x-1.5">
                  {teamMemberships.length === 0 &&
                    user?.personalWorkspace &&
                    user.personalWorkspace.id === workspace?.id && (
                      <Link
                        to="/new"
                        className="h-5 px-1.5 flex text-xs items-center rounded bg-transparent border border-solid border-layer-300 text-layer-700 hover:border-layer-400 hover:text-layer-800 dark:border-layer-700 dark:text-layer-300 dark:hover:border-layer-600 dark:hover:text-layer-200"
                      >
                        <FontAwesomeIcon
                          icon={regular("plus")}
                          className="mr-1 text-xs"
                        />

                        <Box>Create Team</Box>
                      </Link>
                    )}

                  {previewWorkspaces.map((previewWorkspace) => {
                    return (
                      <Tooltip
                        title={`Switch to ${previewWorkspace.name} ${
                          previewWorkspace.id === user.personalWorkspace.id
                            ? "(personal workspace)"
                            : ""
                        }`}
                        placement="top"
                        enterDelay={10}
                        enterNextDelay={0}
                        leaveDelay={500}
                        TransitionProps={{
                          timeout: {
                            enter: 200,
                            exit: 100,
                          },
                          unmountOnExit: true,
                        }}
                        key={previewWorkspace.id}
                        arrow
                      >
                        <Link
                          to={previewWorkspace.route}
                          className="w-5 h-5 overflow-hidden rounded-full hover:opacity-75"
                          onClick={closeWorkspaceMenu}
                          data-cy={`workspace-link-${previewWorkspace.selector}`}
                          data-tour={`workspace-link-${previewWorkspace.selector}`}
                        >
                          {previewWorkspace.logo ? (
                            <img
                              className="w-5 h-5 "
                              src={previewWorkspace.logo}
                            />
                          ) : (
                            <DefaultProfileImage
                              workspaceName={previewWorkspace.name}
                              className="text-xs"
                            />
                          )}
                        </Link>
                      </Tooltip>
                    );
                  })}
                  <WorkspacesSubMenu
                    className="mt-2"
                    placement="left-start"
                    title={
                      <ul
                        className="w-full pb-1"
                        onMouseLeave={() => setIsWorkspacesListOpen(false)}
                      >
                        <li className="flex items-center w-full px-3 pb-1 space-x-2 border-b border-solid h-9 border-layer-200 dark:border-layer-700">
                          <p className="text-sm font-medium select-none text-layer-700 dark:text-layer-300">
                            Switch Workspace
                          </p>
                        </li>

                        <li className="w-full pt-2 max-h-[75vh] overflow-y-auto no-scrollbar">
                          <ul className="flex flex-col w-full space-y-1">
                            <li>
                              <ul>
                                <li className="w-full px-3 pt-1 pb-1">
                                  <p className="text-xs font-semibold select-none text-layer-600 dark:text-layer-300">
                                    Personal Workspace
                                  </p>
                                </li>
                                {personalWorkspaces.map((membership) => {
                                  return (
                                    <Box
                                      key={membership.id}
                                      className="relative w-full group"
                                    >
                                      <IconButtonWithDropdownMenuItem
                                        link={membership}
                                        isOpen={isOpen}
                                      />

                                      <div className="absolute h-full w-8 flex items-center justify-center top-0 right-1.5 pointer-events-none">
                                        {membership.id === workspace?.id ? (
                                          <FontAwesomeIcon
                                            icon={solid("check")}
                                            className="text-sm"
                                          />
                                        ) : (
                                          <FontAwesomeIcon
                                            className="text-sm transition-opacity duration-75 opacity-0 text-layer-800 dark:text-layer-200 group-hover:opacity-100 ease-quick"
                                            icon={solid("arrows-rotate")}
                                          />
                                        )}
                                      </div>
                                    </Box>
                                  );
                                })}
                              </ul>
                            </li>

                            <li>
                              <ul>
                                <li className="w-full px-3 pt-2 pb-1">
                                  <p className="text-xs font-semibold select-none text-layer-600 dark:text-layer-300">
                                    Team Workspaces
                                  </p>
                                </li>
                                {teamWorkspaces.map((membership) => {
                                  return (
                                    <Box
                                      key={membership.id}
                                      className="relative w-full group last-of-type:mb-1"
                                    >
                                      <IconButtonWithDropdownMenuItem
                                        link={membership}
                                        isOpen={isOpen}
                                      />
                                      <div className="absolute h-full w-8 flex items-center justify-center top-0 right-1.5 pointer-events-none">
                                        {membership.id === workspace?.id ? (
                                          <FontAwesomeIcon
                                            icon={solid("check")}
                                            className="text-sm"
                                          />
                                        ) : ![
                                            "new-workspace",
                                            "manage-memberships",
                                          ].includes(membership.id) ? (
                                          <FontAwesomeIcon
                                            className="text-sm transition-opacity duration-75 opacity-0 text-layer-800 dark:text-layer-50 group-hover:opacity-100 ease-quick"
                                            icon={solid("arrows-rotate")}
                                          />
                                        ) : null}
                                      </div>
                                    </Box>
                                  );
                                })}
                              </ul>
                            </li>
                          </ul>
                        </li>
                      </ul>
                    }
                  >
                    <Button
                      onMouseEnter={() => setIsWorkspacesListOpen(true)}
                      className={`h-5 w-5 min-w-[unset] rounded-full transition-colors ease-in-out duration-200 ${
                        isWorkspacesListOpen
                          ? "border border-solid bg-layer-200 border-layer-400 text-layer-800 dark:bg-layer-700 dark:border-layer-400 dark:text-layer-200"
                          : "bg-transparent border border-solid border-layer-300 text-layer-700 hover:border-layer-400 hover:text-layer-800 dark:border-layer-700 dark:text-layer-300 dark:hover:border-layer-600 dark:hover:text-layer-200"
                      }`}
                    >
                      <FontAwesomeIcon
                        className="text-[0.7rem] text-[inherit]"
                        icon={solid("ellipsis")}
                      />
                    </Button>
                  </WorkspacesSubMenu>
                </div>
              </div>

              <div className="flex-1 truncate">
                <h1 className="w-full text-base font-bold truncate cursor-default select-none text-layer-800 dark:text-layer-200">
                  {workspace.displayName}
                </h1>

                <div className="flex items-center space-x-1 text-xs truncate cursor-default text-layer-700 dark:text-layer-300">
                  {workspace.planTier ? (
                    <p>{capitalize(workspace.planTier.toLowerCase())} Plan</p>
                  ) : null}
                  <span className="h-0.5 w-0.5 shrink-0 rounded-full bg-layer-700 dark:bg-layer-400" />
                  {!currentWorkspaceIsPersonal ? (
                    <>
                      {workspaceMemberCount ? (
                        <p
                          className="truncate"
                          title={`${workspaceMemberCount} ${pluralize(
                            "member",
                            workspaceMemberCount,
                          )}`}
                        >
                          {workspaceMemberCount}{" "}
                          {pluralize("member", workspaceMemberCount)}
                        </p>
                      ) : (
                        <p className="truncate">Team workspace</p>
                      )}
                    </>
                  ) : (
                    <p className="truncate" title="Personal workspace">
                      Personal workspace
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </li>

        {adminOptions.length > 0 && (
          <li className="w-full">
            <ul className="w-full pt-0.5 pb-2.5">
              {adminOptions.map((option) => {
                return (
                  <IconButtonWithDropdownMenuItem
                    key={option.id}
                    link={option}
                    isOpen={isOpen}
                    selectorPrefix={"workspace-selection-menu"}
                  />
                );
              })}
            </ul>
          </li>
        )}

        <li className="w-full border-t border-layer-200 dark:border-layer-700">
          <ul className="w-full pt-1.5 pb-1">
            {moreOptions.map((option) => {
              return (
                <IconButtonWithDropdownMenuItem
                  key={option.id}
                  link={option}
                  isOpen={isOpen}
                />
              );
            })}
          </ul>
        </li>
      </Menu>
    </>
  );
}
